import React, { useCallback } from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { ListingPrices } from '@apps/accommodations/AccommodationsConsole.types';
import { AccommodationsConsoleState } from '@apps/accommodations/AccommodationsConsole.types';
import { Skeleton } from '@shared/components/Skeleton';
import { useTranslation } from '@shared/core';

const CostBreakdown = ({ pricingInfo, noOfNights, occupancies }: { pricingInfo: ListingPrices; occupancies: AccommodationsConsoleState['occupancies']; noOfNights: number }) => {
  const { discountPercentage, nightlyBaseRateString, strikeoutNightlyBaseRateString, totalRateString } = pricingInfo;

  const { t } = useTranslation('accommodationsConsole');
  const costTrans = t('listingsPanel', 'costBreakdown');
  const getOccupancyTransText = useCallback(() => {
    if (noOfNights === 1 && occupancies.unitCount === 1) {
      return costTrans.singleRoomSingleNight();
    }
    if (noOfNights === 1 && (occupancies.unitCount || 0) > 1) {
      return costTrans.multiRoomSingleNight({ rooms: occupancies.unitCount });
    }
    if (noOfNights > 1 && occupancies.unitCount === 1) {
      return costTrans.singleRoomMultiNight({ nights: noOfNights });
    }
    return costTrans.multiRoomMultiNight({ nights: noOfNights, rooms: occupancies.unitCount });
  }, [costTrans, noOfNights, occupancies.unitCount]);

  return (
    <Flex flexDirection={'column'} alignItems={'flex-end'}>
      <Flex columnGap={3} alignItems="center">
        {discountPercentage && (
          <Box typographyVariant="label2" borderRadius={2} backgroundColor="#189289" color="white" paddingY={1} paddingX={3}>
            {costTrans.discount({ percentage: discountPercentage })}
          </Box>
        )}
        <Flex columnGap={1} alignItems="center">
          {strikeoutNightlyBaseRateString && (
            <TextV2 textDecoration="line-through" color="mono10" typographyVariant="label2">
              {strikeoutNightlyBaseRateString}
            </TextV2>
          )}
          <TextV2 typographyVariant="hed6" fontWeight={600}>
            {nightlyBaseRateString}
          </TextV2>
          <TextV2 typographyVariant="label2" color="mono10" fontWeight={400}>
            {costTrans.perNight()}
          </TextV2>
        </Flex>
      </Flex>
      <TextV2 marginTop={1} textAlign="end" typographyVariant="label2" color="mono10">
        {costTrans.totalAfterTax({ totalCost: totalRateString })}
      </TextV2>
      <TextV2 marginTop={1} textAlign="end" typographyVariant="label1" color="mono10">
        {getOccupancyTransText()}
      </TextV2>
    </Flex>
  );
};

CostBreakdown.Skeleton = () => {
  return (
    <Flex flexDirection="column" rowGap={'10px'} alignItems="flex-end">
      <Skeleton height={28} width={105} />
      <Skeleton height={16} width={80} />
      <Skeleton height={12} width={210} />
    </Flex>
  );
};

export { CostBreakdown };
